import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import { BackendTypeaheadSelectInput } from '@/components/form/baseInputs/BackendTypeaheadSelectInput/BackendTypeaheadSelectInput';
import { Button } from '@/components/form/baseInputs/Button';
import { Callout } from '@/components/notifications/Callout/Callout';
import { FormProvider } from '@/components/react-hook-form';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { IntegrationInputCard } from '@/modules/assetProviderIntegrations/shared/IntegrationInputCard';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';

import { useSalesforceBrand } from './brand';
import {
  MIN_SALESFORCE_SEARCH_LENGTH,
  useSalesforceHouseholdLink,
} from './hooks/useSalesforceHouseholdLink';

const SEARCH_DEBOUNCE_MS = 300;
const keepServerOptions = <Option,>(options: Option[]) => options;

export function SalesforceHouseholdLinkCard({
  householdId,
}: {
  householdId: string;
}) {
  return (
    <SalesforceHouseholdLinkCardForHousehold
      key={householdId}
      householdId={householdId}
    />
  );
}

function SalesforceHouseholdLinkCardForHousehold({
  householdId,
}: {
  householdId: string;
}) {
  const canManage = useCanManageIntegrationData();
  const state = useSalesforceHouseholdLink(householdId);
  return <SalesforceHouseholdLinkSection state={state} canManage={canManage} />;
}

export function SalesforceHouseholdLinkSection({
  state,
  canManage,
}: {
  state: ReturnType<typeof useSalesforceHouseholdLink>;
  canManage: boolean;
}) {
  const brand = useSalesforceBrand();
  const trackUserEvent = useTrackUserEvent();
  const {
    loading,
    connected,
    link,
    objectApiName,
    results,
    searching,
    formMethods,
    error,
    search,
    clearSearch,
    save,
    loadFailed,
    ambiguous,
    retry,
    defaultSearchTerms,
  } = state;
  const {
    watch,
    setValue,
    formState: { isDirty, isSubmitting: saving },
  } = formMethods;
  const selectedIds = watch('integrationClientIds');
  const [term, setTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchEdited, setSearchEdited] = useState(false);
  const showDefaultResults =
    !searchEdited && !link && !isDirty && defaultSearchTerms.length > 0;
  // The saved link has no record type, so only a new selection shows a chip.
  const [selectedRecordTypeName, setSelectedRecordTypeName] =
    useState<string>();

  useEffect(() => {
    if (isDirty) return;
    setTerm(link?.name ?? '');
    setSearchTerm('');
  }, [isDirty, link]);

  useEffect(() => {
    if (
      showDefaultResults &&
      !loading &&
      connected &&
      objectApiName &&
      !loadFailed &&
      !ambiguous &&
      canManage
    )
      void search(defaultSearchTerms);
  }, [
    showDefaultResults,
    loading,
    connected,
    objectApiName,
    loadFailed,
    ambiguous,
    canManage,
    search,
    defaultSearchTerms,
  ]);

  useDebounce(
    () => {
      if (searchTerm.trim().length >= MIN_SALESFORCE_SEARCH_LENGTH)
        void search(searchTerm);
    },
    SEARCH_DEBOUNCE_MS,
    [searchTerm]
  );

  const hasSearchTerm = Boolean(searchTerm.trim());
  let noOptionsText = 'No matching records';
  if (showDefaultResults)
    noOptionsText = 'No suggested matches. Search by name.';
  else if (term.trim().length < MIN_SALESFORCE_SEARCH_LENGTH)
    noOptionsText = `Type at least ${MIN_SALESFORCE_SEARCH_LENGTH} characters of a name`;
  const options =
    hasSearchTerm || showDefaultResults
      ? results.map(({ id, name, recordTypeName }) => ({
          value: id,
          display: name,
          badgeText: recordTypeName,
        }))
      : selectedIds.map((id) => ({
          value: id,
          display: term,
          badgeText: selectedRecordTypeName,
        }));

  if (loading || (!connected && !loadFailed)) return null;

  function selectRecord(recordId: string) {
    const record = results.find(({ id }) => id === recordId);
    if (!record) return;
    setValue('integrationClientIds', [record.id], { shouldDirty: true });
    setTerm(record.name);
    setSelectedRecordTypeName(record.recordTypeName);
    setSearchEdited(true);
    setSearchTerm('');
    clearSearch();
    trackUserEvent('integration salesforce_record_selected', {
      recordId: record.id,
      source: showDefaultResults ? 'suggested' : 'manual',
    });
  }

  return (
    <IntegrationInputCard providerName={brand.name} variant="outlined">
      <FormProvider {...formMethods}>
        <Stack
          spacing={2}
          role="region"
          aria-label={`${brand.name} data link`}
          aria-busy={saving || searching}
        >
          {error && (
            <Callout severity="error">
              <span role="alert">{error}</span>
            </Callout>
          )}

          {loadFailed && (
            <Button onClick={retry} size="sm" variant="secondary">
              Retry
            </Button>
          )}

          {connected && !objectApiName && (
            <Callout severity="info-low">
              An administrator must choose the {brand.name} object for
              households before this household can be linked.
            </Callout>
          )}

          {objectApiName && !canManage && <LinkSummary link={link} />}

          {objectApiName && !loadFailed && !ambiguous && canManage && (
            <>
              <BackendTypeaheadSelectInput<string>
                label={`Data link ${objectApiName}`}
                hideLabel
                value={hasSearchTerm ? '' : (selectedIds[0] ?? '')}
                inputValue={term}
                options={options}
                filterOptions={keepServerOptions}
                loading={searching}
                disableClearable={!selectedIds.length}
                placeholder="Search by name"
                helpText={
                  showDefaultResults
                    ? 'Suggested matches based on your clients'
                    : undefined
                }
                noOptionsText={noOptionsText}
                onInputChange={(value, reason) => {
                  if (reason === 'clear') {
                    setSearchEdited(true);
                    setTerm('');
                    setSearchTerm('');
                    setValue('integrationClientIds', [], { shouldDirty: true });
                    clearSearch();
                    return;
                  }
                  if (reason !== 'input') return;
                  setSearchEdited(true);
                  setTerm(value);
                  setSearchTerm(value);
                  if (!value)
                    setValue('integrationClientIds', [], { shouldDirty: true });
                  clearSearch();
                }}
                onChange={(_event, recordId, reason) => {
                  if (reason === 'clear') {
                    setValue('integrationClientIds', [], { shouldDirty: true });
                    return;
                  }
                  if (reason === 'selectOption' && recordId)
                    selectRecord(recordId);
                }}
                disabled={saving}
              />
              {link && isDirty && !selectedIds.length && (
                <Callout severity="error">
                  Saving this change unlinks the {brand.name} record from this
                  household. Nothing in {brand.name} is deleted.
                </Callout>
              )}
              <Button
                size="sm"
                variant="primary"
                disabled={saving || !isDirty}
                loading={saving}
                onClick={save}
              >
                Save
              </Button>
            </>
          )}
        </Stack>
      </FormProvider>
    </IntegrationInputCard>
  );
}

function LinkSummary({
  link,
}: {
  link: ReturnType<typeof useSalesforceHouseholdLink>['link'];
}) {
  const brand = useSalesforceBrand();
  if (!link)
    return (
      <Typography role="status" variant="body-s">
        Not linked to {brand.name}.
      </Typography>
    );
  return (
    <Stack spacing={0.5}>
      <Typography role="status" variant="body-s">
        Saved {brand.name} selection
      </Typography>
      <Typography variant="body-s" sx={{ overflowWrap: 'anywhere' }}>
        {link.name}
      </Typography>
    </Stack>
  );
}
